import { useState } from 'react';

const requests = [
  {
    id: 'MR-2026-014',
    title: 'HVAC Filter Replacement',
    category: 'HVAC',
    priority: 'Routine',
    status: 'In Progress',
    submitted: 'Mar 22, 2026',
    updated: 'Mar 28, 2026',
    description: 'Scheduled quarterly HVAC filter replacement and system inspection. Technician appointment confirmed for April 3rd.',
    assignedTo: 'CoolAir Services',
    estimatedCost: '$180',
    icon: 'ri-temp-cold-line',
  },
  {
    id: 'MR-2026-011',
    title: 'Terrace Drain Cleaning',
    category: 'Plumbing',
    priority: 'Routine',
    status: 'Completed',
    submitted: 'Mar 10, 2026',
    updated: 'Mar 15, 2026',
    description: 'Rooftop terrace drainage cleared of seasonal debris. All drains tested and confirmed clear.',
    assignedTo: 'PrimePlumb Co.',
    estimatedCost: '$270',
    icon: 'ri-drop-line',
  },
  {
    id: 'MR-2026-008',
    title: 'Smart Lock Battery Replacement',
    category: 'Security',
    priority: 'Urgent',
    status: 'Completed',
    submitted: 'Feb 18, 2026',
    updated: 'Feb 19, 2026',
    description: 'Front door smart lock battery depleted. Replaced with premium long-life batteries. System tested and operational.',
    assignedTo: 'SecureTech Solutions',
    estimatedCost: '$45',
    icon: 'ri-lock-line',
  },
  {
    id: 'MR-2026-003',
    title: 'Kitchen Faucet Repair',
    category: 'Plumbing',
    priority: 'Standard',
    status: 'Completed',
    submitted: 'Jan 29, 2026',
    updated: 'Feb 02, 2026',
    description: 'Minor drip from kitchen faucet cartridge. Cartridge replaced, all connections re-sealed and tested.',
    assignedTo: 'PrimePlumb Co.',
    estimatedCost: '$135',
    icon: 'ri-drop-line',
  },
];

const statusColors: Record<string, string> = {
  'In Progress': 'text-amber-600 bg-amber-50',
  'Completed': 'text-emerald-600 bg-emerald-50',
  'Scheduled': 'text-[#8B7355] bg-[#F5F0E8]',
};

const priorityColors: Record<string, string> = {
  'Urgent': 'text-red-600 bg-red-50',
  'Standard': 'text-[#6B6B6B] bg-[#F5F3F0]',
  'Routine': 'text-[#9A9A9A] bg-[#FAFAF8]',
};

export default function MaintenanceTab() {
  const [selected, setSelected] = useState<string | null>('MR-2026-014');
  const selectedRequest = requests.find((r) => r.id === selected);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Maintenance
          </h2>
          <p className="text-sm text-[#6B6B6B] font-light mt-1">Track all maintenance requests and service history</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
            <span className="w-2 h-2 rounded-full bg-amber-400 inline-block"></span>
            1 In Progress
          </div>
          <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
            <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block"></span>
            3 Completed
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* List */}
        <div className="lg:col-span-2 flex flex-col gap-3">
          {requests.map((req) => (
            <div
              key={req.id}
              onClick={() => setSelected(req.id)}
              className={`bg-white border rounded-lg p-4 cursor-pointer transition-all ${
                selected === req.id ? 'border-[#2C2C2C]' : 'border-[#E8E6E3] hover:border-[#8B7355]'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-md bg-[#F5F3F0] flex-shrink-0">
                  <i className={`${req.icon} text-[#8B7355] text-sm`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-[#1A1A1A] leading-tight">{req.title}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 ${statusColors[req.status]}`}>
                      {req.status}
                    </span>
                  </div>
                  <p className="text-xs text-[#9A9A9A] mt-1">{req.id} · {req.submitted}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Detail */}
        {selectedRequest && (
          <div className="lg:col-span-3 bg-white border border-[#E8E6E3] rounded-lg p-6 flex flex-col gap-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs text-[#9A9A9A] mb-1">{selectedRequest.id}</p>
                <h3 className="text-lg font-semibold text-[#1A1A1A]">{selectedRequest.title}</h3>
              </div>
              <span className={`text-xs px-3 py-1.5 rounded-full font-medium whitespace-nowrap ${statusColors[selectedRequest.status]}`}>
                {selectedRequest.status}
              </span>
            </div>

            <p className="text-sm text-[#5A5A5A] font-light leading-relaxed">{selectedRequest.description}</p>

            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Category', value: selectedRequest.category },
                { label: 'Priority', value: selectedRequest.priority, badge: priorityColors[selectedRequest.priority] },
                { label: 'Submitted', value: selectedRequest.submitted },
                { label: 'Last Updated', value: selectedRequest.updated },
                { label: 'Assigned To', value: selectedRequest.assignedTo },
                { label: 'Est. Cost', value: selectedRequest.estimatedCost },
              ].map((item) => (
                <div key={item.label} className="p-3 bg-[#FAFAF8] rounded-md">
                  <p className="text-xs text-[#9A9A9A] mb-1">{item.label}</p>
                  {item.badge ? (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.badge}`}>{item.value}</span>
                  ) : (
                    <p className="text-sm font-medium text-[#1A1A1A]">{item.value}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Timeline */}
            <div>
              <p className="text-xs font-semibold text-[#1A1A1A] uppercase tracking-[0.1em] mb-3">Status Timeline</p>
              <div className="flex flex-col gap-0">
                {[
                  { label: 'Request Submitted', date: selectedRequest.submitted, done: true },
                  { label: 'Vendor Assigned', date: selectedRequest.updated, done: true },
                  { label: selectedRequest.status === 'Completed' ? 'Work Completed' : 'Work In Progress', date: selectedRequest.status === 'Completed' ? selectedRequest.updated : 'Pending', done: selectedRequest.status === 'Completed' },
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${step.done ? 'bg-[#2C2C2C]' : 'bg-[#E8E6E3]'}`}>
                        {step.done ? <i className="ri-check-line text-white text-xs"></i> : <div className="w-2 h-2 rounded-full bg-[#AEAAA4]"></div>}
                      </div>
                      {i < 2 && <div className={`w-px h-6 ${step.done ? 'bg-[#2C2C2C]' : 'bg-[#E8E6E3]'}`}></div>}
                    </div>
                    <div className="pb-4">
                      <p className={`text-xs font-medium ${step.done ? 'text-[#1A1A1A]' : 'text-[#AEAAA4]'}`}>{step.label}</p>
                      <p className="text-xs text-[#9A9A9A]">{step.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
